"use client";

import { useEffect, useRef } from "react";
import {
  type ActiveChip,
  type FilterControllerProps,
  type FilterMenuItem,
  SingleSelectFilterMenu,
  useNamespacedTranslations,
  useRegisterFilter,
  useSingleValueParameter,
} from "@zapier/shared";
import { CONNECTION_STATUS_NON_COMPLIANT } from "@zapier/utils";
import { CONNECTION_STATUS_DISABLED } from "../../constants";

type StatusValue =
  | "active"
  | "expired"
  | typeof CONNECTION_STATUS_DISABLED
  | typeof CONNECTION_STATUS_NON_COMPLIANT;

const VALID_STATUS = new Set<StatusValue>([
  "active",
  "expired",
  CONNECTION_STATUS_DISABLED,
  CONNECTION_STATUS_NON_COMPLIANT,
]);

const ICON = "statusStatus";
const CHIP_KEY_BY_STATUS: Record<StatusValue, string> = {
  active: "active",
  expired: "expired",
  [CONNECTION_STATUS_DISABLED]: "disabled",
  [CONNECTION_STATUS_NON_COMPLIANT]: "nonCompliant",
};

// `?status=non-compliant` represents the managed-auth-violation state.
// `useRouterParams.setParams` strips `managedAuthViolation` on every write, so
// atomicity between the two params is preserved by writing the token alone.
export function ConnectionStatusFilterController({
  isOpen,
  namespace,
  onClose,
  onInteraction,
}: FilterControllerProps): JSX.Element | null {
  const t = useNamespacedTranslations(namespace);
  const status = useSingleValueParameter<StatusValue>("status", {
    validValues: VALID_STATUS,
  });

  // Defer close until the URL reflects the new value (matches Zaps' pattern).
  const closeOnNextValue = useRef(false);
  useEffect(() => {
    if (closeOnNextValue.current && status.value) {
      closeOnNextValue.current = false;
      onClose();
    }
  }, [status.value, onClose]);

  const hasValue = !!status.value;
  const chipKey = status.value ? CHIP_KEY_BY_STATUS[status.value] : undefined;
  const label = chipKey ? t(`status.chipLabel.${chipKey}`) : "";
  const ariaLabel = chipKey ? t(`status.chipAriaLabel.${chipKey}`) : "";

  let chip: ActiveChip | undefined;
  let signature = "status:none";
  if (hasValue) {
    chip = {
      key: "status",
      iconName: ICON,
      title: t("status.label"),
      label,
      ariaLabel,
    };
    signature = `status:${status.value}`;
  } else if (isOpen) {
    chip = {
      key: "status",
      iconName: ICON,
      title: t("status.label"),
      ariaLabel: t("status.label"),
      isDraft: true,
    };
    signature = "status:draft";
  }

  const items: FilterMenuItem[] = [
    { id: "active", name: t("status.active") },
    { id: "expired", name: t("status.expired") },
    { id: CONNECTION_STATUS_DISABLED, name: t("status.disabled") },
    {
      id: CONNECTION_STATUS_NON_COMPLIANT,
      name: t("status.nonCompliant"),
    },
  ];

  useRegisterFilter({
    type: "status",
    hasValue,
    signature,
    chip,
    renderMenu: () => (
      <SingleSelectFilterMenu
        label={t("status.label")}
        items={items}
        selectedId={status.value}
        onSelect={(id) => {
          if (!VALID_STATUS.has(id as StatusValue)) {
            onClose();
            return;
          }
          if (id === status.value) {
            onClose();
            return;
          }
          closeOnNextValue.current = true;
          status.set(id as StatusValue);
          onInteraction?.();
        }}
      />
    ),
    // React Aria submenu (Filters-button path): RA closes the tree on select.
    menuOptions: {
      label: t("status.label"),
      items,
      selectedId: status.value,
      onSelect: (id) => {
        if (VALID_STATUS.has(id as StatusValue) && id !== status.value) {
          status.set(id as StatusValue);
          onInteraction?.();
        }
      },
    },
  });
  // eslint-disable-next-line unicorn/no-null -- headless controller renders nothing
  return null;
}
